import React, { useEffect, useState, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDownload,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiPhone
} from "react-icons/fi";
import "../styles/DroneBookingStatus.css";

const GET_DRONE_BOOKINGS = gql`
  query {
    getAllDroneRentals {
      drone_rental_id
      name
      phone
      location
      rentalId
      rentalDate
      sellerId
      status
      createdAt
    }
  }
`;

export default function DroneBookingStatus() {
  const { data, loading, error, refetch } = useQuery(GET_DRONE_BOOKINGS, {
    fetchPolicy: "network-only"
  });
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    if (data?.getAllDroneRentals) {
      const processedBookings = data.getAllDroneRentals
        .filter(b => b.status?.toLowerCase() !== "cancelled")
        .map(b => ({
          ...b,
          status: b.status?.toLowerCase()
        }));
      
      setBookings(processedBookings);
      setFilteredBookings(processedBookings);
    }
  }, [data]);

  // Apply filters
  useEffect(() => {
    let results = [...bookings];
    
    // Search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(b => 
        b.name?.toLowerCase().includes(term) ||
        b.phone?.toLowerCase().includes(term) ||
        b.location?.toLowerCase().includes(term) ||
        b.rentalId?.toLowerCase().includes(term) ||
        b.sellerId?.toLowerCase().includes(term) ||
        b.drone_rental_id?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== "all") {
      results = results.filter(b => b.status === statusFilter);
    }
    
    // Date filter
    if (dateFilter) {
      results = results.filter(b => {
        const bookingDate = parseDate(b.rentalDate || b.createdAt);
        if (!bookingDate) return false;
        const filterDate = new Date(dateFilter);
        return bookingDate.toDateString() === filterDate.toDateString();
      });
    }
    
    // Seller filter
    if (sellerFilter) {
      results = results.filter(b => 
        b.sellerId?.toLowerCase().includes(sellerFilter.toLowerCase())
      );
    }
    
    setFilteredBookings(results);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateFilter, sellerFilter, bookings]);

  const parseDate = (value) => {
    if (!value) return null;
    if (!isNaN(value)) {
      if (value.toString().length === 10) return new Date(value * 1000);
      return new Date(Number(value));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "—";
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Statistics
  const stats = useMemo(() => {
    const total = bookings.length;
    const approved = bookings.filter(b => b.status === "approved" || b.status === "confirmed").length;
    const pending = bookings.filter(b => b.status === "pending").length;
    const rejected = bookings.filter(b => b.status === "rejected").length;
    
    return { total, approved, pending, rejected };
  }, [bookings]);

  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredBookings.slice(startIndex, endIndex);

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setDateFilter("");
    setSellerFilter("");
  };

  const exportToCSV = () => {
    const headers = [
      "Booking ID",
      "Customer",
      "Phone",
      "Location",
      "Rental Date",
      "Status",
      "Created"
    ];

    const rows = filteredBookings.map(b => [
      b.drone_rental_id,
      `"${b.name || ""}"`,
      `"${b.phone || ""}"`,
      `"${b.location || ""}"`,
      formatDate(b.rentalDate),
      b.status?.charAt(0).toUpperCase() + b.status?.slice(1),
      formatDateTime(b.createdAt)
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `drone-bookings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="booking-loading">
      <div className="premium-spinner"></div>
      <p>Loading bookings...</p>
    </div>
  );
  
  if (error) return (
    <div className="booking-error">
      <div className="error-icon">
        <FiAlertCircle />
      </div>
      <p>Error loading data</p>
      <button onClick={() => refetch()} className="retry-btn">
        Try Again
      </button>
    </div>
  );

  return (
    <div className="booking-container premium">
      {/* Premium Header */}
      <div className="premium-header">
        <div className="header-main">
          <h1 className="premium-title">
            <FiTrendingUp className="title-icon" />
            Drone Bookings
          </h1>
          <p className="premium-subtitle">Manage and track all drone booking requests</p>
        </div>
        
        <div className="header-stats">
          <div className="stat-mini total">
            <div className="stat-mini-value">{stats.total}</div>
            <div className="stat-mini-label">Total</div>
          </div>
          <div className="stat-mini approved">
            <div className="stat-mini-value">{stats.approved}</div>
            <div className="stat-mini-label">Approved</div>
          </div>
          <div className="stat-mini pending">
            <div className="stat-mini-value">{stats.pending}</div>
            <div className="stat-mini-label">Pending</div>
          </div>
          <div className="stat-mini rejected">
            <div className="stat-mini-value">{stats.rejected}</div>
            <div className="stat-mini-label">Rejected</div>
          </div>
        </div>
        
        <div className="header-controls">
          <button className="premium-btn export" onClick={exportToCSV}>
            <FiDownload /> Export
          </button>
          <button className="premium-btn refresh" onClick={() => refetch()}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Premium Filter Bar */}
      <div className="premium-filter-bar">
        <div className="search-wrapper">
          <div className="search-icon">
            <FiSearch />
          </div>
          <input
            type="text"
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="premium-search"
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <button 
            className={`filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>
          <div className="page-control">
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="premium-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="premium-advanced-filters">
          <div className="filter-row">
            <div className="filter-col">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-select"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            <div className="filter-col">
              <label>Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <label>Seller</label>
              <input
                type="text"
                placeholder="Seller ID"
                value={sellerFilter}
                onChange={(e) => setSellerFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <button className="reset-filters" onClick={resetFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Table */}
      <div className="premium-table-container">
        <div className="table-info-bar">
          <div className="table-count">
            {filteredBookings.length} bookings
          </div>
          <div className="table-navigation">
            Showing {Math.min(startIndex + 1, filteredBookings.length)}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length}
          </div>
        </div>
        
        <div className="table-scroll-wrapper">
          <table className="premium-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Customer</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Rental Date</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    <FiAlertCircle />
                    <p>No bookings found</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((booking) => (
                  <tr 
                    key={booking.drone_rental_id} 
                    className="booking-row"
                    onClick={() => setSelectedBooking(booking)}
                  >
                    <td>
                      <div className="booking-id">#{booking.drone_rental_id}</div>
                      <div className="booking-date">{formatDate(booking.createdAt)}</div>
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">
                          <FiUser /> {booking.name || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="phone-info">
                        <div className="phone">
                          <FiPhone /> {booking.phone || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="location-info">
                        <div className="location">
                          <FiMapPin /> {booking.location || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="date-info">
                        <div className="date">
                          <FiCalendar /> {formatDate(booking.rentalDate)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || "—"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="created">
                        {formatDateTime(booking.createdAt)}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Premium Pagination */}
        {totalPages > 1 && (
          <div className="premium-pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-nav">
              <button 
                className="nav-btn first"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <FiChevronsLeft />
              </button>
              <button 
                className="nav-btn prev"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return pageNum <= totalPages ? (
                  <button
                    key={pageNum}
                    className={`nav-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ) : null;
              })}
              
              <button 
                className="nav-btn next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FiChevronRight />
              </button>
              <button 
                className="nav-btn last"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Modal */}
      {selectedBooking && (
        <div className="premium-modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="premium-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Booking Details</h3>
              <button className="modal-close" onClick={() => setSelectedBooking(null)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Booking ID</span>
                  <span className="detail-value">#{selectedBooking.drone_rental_id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Customer</span>
                  <span className="detail-value">
                    <FiUser /> {selectedBooking.name}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">
                    <FiPhone /> {selectedBooking.phone}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">
                    <FiMapPin /> {selectedBooking.location}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Rental Date</span>
                  <span className="detail-value">
                    <FiCalendar /> {formatDate(selectedBooking.rentalDate)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Seller ID</span>
                  <span className="detail-value">{selectedBooking.sellerId || "—"}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`detail-value status ${selectedBooking.status}`}>
                    {selectedBooking.status?.charAt(0).toUpperCase() + selectedBooking.status?.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">{formatDateTime(selectedBooking.createdAt)}</span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-action-btn" onClick={() => setSelectedBooking(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}